import "./common.css";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RESEND_OTP_TIMEOUT } from "../../components/common/constant";
import "../../pages/login/loginPage.css";
import "../../components/common/style.css";
import "../../components/common/responsive.css";
import { useDispatch } from "react-redux";
import { useApi } from "../../shared/api";
import { LoggedUser, setLoggedUser } from '../../redux/slices/auth';
import { useUser } from "../../contexts/userContext";
import { resendOtpClickedEvent } from "../../shared/events/additional.event";
import { sendOtpClickedEvent } from "../../shared/events/login.event";
export interface VerifyOtpProps {
  userId: number;
  userInfo?: any;
  mobileNo?: number | any;
  showEditButton?: boolean;
  handleAfterOptSuccess?: (bool: boolean) => void
  onClose?: () => void;
  focusTrigger?: number;
  onlyVerifyNotProceedWithLogin?: boolean;
  isVerifyOtpModalOpen?: boolean;
  setCurrentView?: (view: 'login' | 'register' | 'verifyOtp') => void;
}

export const VerifyOtp = ({ isVerifyOtpModalOpen, onlyVerifyNotProceedWithLogin, userId, mobileNo, handleAfterOptSuccess, onClose, focusTrigger = 0, setCurrentView, showEditButton = true }: VerifyOtpProps) => {
  const otpLength = 4;
  const API = useApi();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [hasOtpError, setHasOtpError] = useState(false);
  const [otp, setOtp] = useState<string[]>(new Array(otpLength).fill(""));
  const [isDisabled, setIsDisabled] = useState(true);
  const [seconds, setSeconds] = useState(60);
  const [expireSeconds, setExpireSeconds] = useState(120);
  const [interval, setinterval] = useState<NodeJS.Timeout | null>(null);
  const { setUserAuthorized } = useUser()
  const dispatch = useDispatch()
  const user_id = userId;
  const mobileNumber = location.state?.mobile_no || mobileNo
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const expireCalledRef = useRef(false);


  useEffect(() => {
    expireCalledRef.current = false;
    let interval: NodeJS.Timeout

    if (isVerifyOtpModalOpen == undefined) {
      interval = setInterval(() => {
        setSeconds(prev => {
          const next = Math.max(prev - 1, 0);
          if (next === 0) {
            setIsDisabled(false);
          }
          return next;
        });
        setinterval(interval)
        // update expireSeconds (OTP expiry)
        setExpireSeconds(prev => {
          const next = Math.max(prev - 1, 0);
          if (next === 0 && !expireCalledRef.current) {
            expireCalledRef.current = true;
            handleResendExpireOtp();
          }
          return next;
        });
      }, 1000);

    } else if (isVerifyOtpModalOpen) {
      interval = setInterval(() => {
        setSeconds(prev => {
          const next = Math.max(prev - 1, 0);
          if (next === 0) {
            setIsDisabled(false);
          }
          return next;
        });
        setinterval(interval)

        // update expireSeconds (OTP expiry)
        setExpireSeconds(prev => {
          const next = Math.max(prev - 1, 0);
          if (next === 0 && !expireCalledRef.current) {
            expireCalledRef.current = true;
            handleResendExpireOtp();
          }
          return next;
        });
      }, 1000);
    }

    return () => {
      clearInterval(interval);
      setSeconds(60)
    };
  }, [isVerifyOtpModalOpen]);

  useEffect(() => {
    // Aggressive focus strategy for iOS
    const attemptFocus = () => {
      otpInputRefs.current[0]?.focus();
    };

    attemptFocus(); // Immediate attempt

    // Retry multiple times
    const intervalId = setInterval(attemptFocus, 100);
    const timeoutId = setTimeout(() => clearInterval(intervalId), 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [focusTrigger]);


  useEffect(() => {

    if (interval && typeof isVerifyOtpModalOpen == 'boolean' && !isVerifyOtpModalOpen) {
      clearInterval(interval)
    }

  }, [interval, isVerifyOtpModalOpen])


  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      }
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  }

  const handleOtpSubmit = async (otpValue: any) => {
    if (otpValue.length !== otpLength) {
      setError('Please enter a 4-digit OTP');
      return;
    }

    try {
      const verifyOtpPayload = { userId: user_id as number, otp: otpValue }
      const resJson = await API.API_V1.auth.postVerifyOtp(verifyOtpPayload)
      if (resJson.data.success) {
        sendOtpClickedEvent(mobileNumber, location.pathname);
        const optResponse = resJson.data;
        const { id, name, mobile_no, referral_code, remember_token } = optResponse.user
        const LoggedInUser: LoggedUser = {
          authToken: remember_token,
          userId: id,
          name: name,
          mobileNumber: mobile_no,
          referral_code: referral_code,
          email: null,
          profileComplete: Boolean(optResponse.profile_completed),
          isUserLoggedOut: false,
          openLoginModal: Boolean(optResponse.profile_completed)
        }
        setUserAuthorized(true)
        if (!onlyVerifyNotProceedWithLogin)
          dispatch(setLoggedUser(LoggedInUser))

        if (LoggedInUser.profileComplete == true) {
          onClose?.()
        }
        if (handleAfterOptSuccess) handleAfterOptSuccess(true)
      } else {
        setOtp(new Array(otpLength).fill('')); // Reset OTP fields
        setError(resJson?.data.message || 'Invalid OTP. Please verify and try again.');
        setHasOtpError(true); // Set error state for visual feedback
        otpInputRefs.current[0]?.focus(); // Focus the first input
      }
    } catch (err: any) {
      setOtp(new Array(otpLength).fill('')); // Reset OTP fields
      setError(err.message || 'An unexpected error occurred');
      setHasOtpError(true); // Set error state for visual feedback
    }
  };

  const handleResendOtp = async () => {
    try {
      resendOtpClickedEvent(mobileNo);
      setIsDisabled(true);
      setSeconds(RESEND_OTP_TIMEOUT);
      setExpireSeconds(120)
      setHasOtpError(false);
      setError(null);
      setOtp(new Array(otpLength).fill(''))
      const response = await API.API_V1.auth.resendOtp({ userInfo: { id: user_id as number } })
      otpInputRefs.current[0]?.focus();
    } catch (error) {
      setError('Failed to resend OTP');
    }
  };

  const handleChangeOtp = (element: HTMLInputElement, index: number) => {
    const value = element.value.replace(/\D/g, "");

    // Clear error state when user starts typing
    if (hasOtpError) {
      setHasOtpError(false);
      setError(null);
    }

    if (value.length === 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      const otpValue = newOtp.join("");

      if (index < otp.length - 1) {
        otpInputRefs.current[index + 1]?.focus();
      }
      if (otpValue.length === otpLength) {
        handleOtpSubmit(otpValue)
      }

    } else if (value === "") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  }

  const handleResendExpireOtp = async () => {
    try {
      const response = await API.API_V1.auth.patchOtpExpire({ userInfo: { id: user_id as number } })
    } catch (error) {
      setError('Failed to resend OTP');
    }
  };

  const convert_resend_button_timer = () => {
    const minutes = Math.floor(seconds / 60);
    const secondsRemaining = seconds % 60;
    if (secondsRemaining == 0) {
      return `60`
    }
    return `${secondsRemaining
      .toString()
      }`; // padStart(2, '0')
  };




  return (

    <div className="verify-otp-content- form-container vryfi_OTP">
      <p className="vryfi_OTP_heading">
        Enter the OTP sent to  <span className="sent_otp_number">(+91)-{
          mobileNumber && !isNaN(Number(mobileNumber)) && mobileNumber.length === 10
            ? `${mobileNumber.slice(0, 2)}xxxxxx${mobileNumber.slice(-2)}`
            : '95-xxxxxx-89'
        }</span>
        {showEditButton && <a href="#" style={{ marginLeft: "5px" }} onClick={() => setCurrentView && setCurrentView('login')}>Edit</a>}
      </p>

      <div className="verify-rep- otp-input-container flex-define ">
        {otp.map((digit, index) => (
          <input
            key={index}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            className={`otp-input ${hasOtpError ? 'otp-input-error' : ''}`}
            maxLength={1}
            value={digit}
            placeholder="_"
            ref={(el) => (otpInputRefs.current[index] = el)}
            onChange={(e) => handleChangeOtp(e.target, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={(e) => e.target.select()}
            autoFocus={index === 0}
          />
        ))}
      </div>
      <div style={{ height: "25px" }}>
        {error && <div className="error-verify-fail" style={{ color: "#666", fontSize: "13px", textAlign: "left" }}>{error}</div>}
      </div>
      <div className="verify_Otp_btn_wrap-">
        <div className="social-buttons">
          <button
            className="btn btn-secondary- btn-outline-success re_send_otp"
            onClick={handleResendOtp}
            disabled={isDisabled}
            style={{
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <div className="otptext">
              <p className="request" style={{ color: "#666", fontSize: "13px", textAlign: "left" }}>
                {isDisabled
                  ? <span>Didn't receive the OTP? Retry in <span style={{ color: "#c10202ff", }}> {` ${convert_resend_button_timer()}`} s</span></span>
                  : "Didn't receive the OTP? "}
              </p>
            </div>

            <div className="resend_otp" style={{ height: "25px" }}>
              {
                !isDisabled && (
                  <span>
                    <i className="far fa-comment-alt"></i> Resend OTP
                  </span>
                )
              }
            </div>


          </button>
          <button
            className="btn btn-success verify_Otp"
            onClick={handleOtpSubmit}
            disabled
            style={{ display: 'none' }} // Hide the button as it's not needed
          >
            Verify OTP
          </button>
        </div>
      </div>
      <p className="trouble_otp">Trouble in getting OTP?</p>
      <p className="chk_mobile_no">
        Make sure you entered the correct mobile number.
      </p>


    </div>
  );
}


